import { useApp } from '@/context/AppContext';
import { PLUGINS, PLUGIN_CATEGORIES } from '@/plugins/registry';
import { Badge } from '@/components/ui/Badge';
import { pushSupported } from '@/lib/push';
import { useState, useCallback, useEffect } from 'react';
import { supabase } from '@/data/supabase';

// Toggle switch component
function Toggle({ on, disabled, onChange }) {
  return (
    <div
      onClick={disabled ? undefined : onChange}
      style={{
        width: 40,
        height: 22,
        borderRadius: 11,
        background: on ? 'var(--teal)' : 'var(--s3)',
        position: 'relative',
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'background .2s',
        flexShrink: 0,
        opacity: disabled ? 0.6 : 1,
        border: '1px solid var(--b2)',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 2,
          left: on ? 19 : 2,
          width: 16,
          height: 16,
          borderRadius: '50%',
          background: 'white',
          transition: 'left .2s',
          boxShadow: '0 1px 3px rgba(0,0,0,.3)',
        }}
      />
    </div>
  );
}

// Category icon mapping
const CAT_ICONS = {
  core:       '⚙️',
  ai:         '🤖',
  field:      '📱',
  monitoring: '📡',
  business:   '💼',
  analytics:  '📊',
};

// ─── Provider Status Card ─────────────────────────────────────
const PROVIDERS = [
  {
    id: 'resend_email',
    label: 'Resend (Email)',
    fn: 'send-message',
    body: { action: 'ping', channel: 'email' },
    hint: 'Set RESEND_API_KEY + RESEND_FROM_EMAIL',
  },
  {
    id: 'twilio_sms',
    label: 'Twilio (SMS)',
    fn: 'send-message',
    body: { action: 'ping', channel: 'sms' },
    hint: 'Set TWILIO_ACCOUNT_SID + TWILIO_AUTH_TOKEN + TWILIO_FROM_NUMBER',
  },
  {
    id: 'google_maps',
    label: 'Google Maps',
    fn: 'geocode-address',
    body: { action: 'ping' },
    hint: 'Set GOOGLE_MAPS_API_KEY (enable Geocoding API + Routes API)',
  },
  {
    id: 'supabase_storage',
    label: 'Supabase Storage (Photos)',
    fn: 'photo-upload-url',
    body: { action: 'ping' },
    hint: 'Built into every Supabase project — no extra secrets needed.',
  },
];

function ProviderChip({ status, label }) {
  const color = status === 'ok'
    ? 'var(--teal)'
    : status === 'error'
    ? 'var(--rose)'
    : 'var(--t3)';
  const bg = status === 'ok'
    ? 'var(--teal-t)'
    : status === 'error'
    ? 'var(--rose-t)'
    : 'var(--s3)';
  const dot = status === 'ok' ? '●' : status === 'error' ? '●' : '○';
  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 5,
      padding: '3px 9px',
      borderRadius: 99,
      fontSize: 11,
      fontWeight: 600,
      color,
      background: bg,
      border: `1px solid ${color}33`,
    }}>
      <span style={{ fontSize: 8 }}>{dot}</span>
      {label}
    </span>
  );
}

function ProviderStatusCard() {
  const [statuses, setStatuses] = useState(
    () => Object.fromEntries(PROVIDERS.map(p => [p.id, 'idle']))
  );
  const [details, setDetails] = useState({});
  const [checking, setChecking] = useState(false);

  const checkAll = useCallback(async () => {
    if (!supabase) {
      setStatuses(Object.fromEntries(PROVIDERS.map(p => [p.id, 'error'])));
      setDetails(Object.fromEntries(PROVIDERS.map(p => [p.id, 'Supabase not connected'])));
      return;
    }
    setChecking(true);
    setStatuses(Object.fromEntries(PROVIDERS.map(p => [p.id, 'checking'])));

    const results = await Promise.all(
      PROVIDERS.map(async (prov) => {
        try {
          const { data, error } = await supabase.functions.invoke(prov.fn, {
            body: prov.body,
          });
          if (error) {
            // error.context may contain the actual JSON body for non-2xx
            const msg = error?.context?.error || error.message || 'Unknown error';
            const isUnconfigured = typeof msg === 'string' && msg.toLowerCase().includes('not configured');
            return { id: prov.id, status: 'error', detail: isUnconfigured ? 'Not configured' : msg };
          }
          if (data?.status === 'ok') {
            const extra = data.from ? ` · from: ${data.from}` : data.bucket ? ` · bucket: ${data.bucket}` : '';
            return { id: prov.id, status: 'ok', detail: `Active${extra}` };
          }
          const errMsg = data?.error || 'Unexpected response';
          const isUnconfigured = typeof errMsg === 'string' && errMsg.toLowerCase().includes('not configured');
          return { id: prov.id, status: 'error', detail: isUnconfigured ? 'Not configured' : errMsg };
        } catch (e) {
          return { id: prov.id, status: 'error', detail: String(e) };
        }
      })
    );

    const newStatuses = {};
    const newDetails = {};
    for (const r of results) {
      newStatuses[r.id] = r.status;
      newDetails[r.id] = r.detail;
    }
    setStatuses(newStatuses);
    setDetails(newDetails);
    setChecking(false);
  }, []);

  const anyChecked = Object.values(statuses).some(s => s !== 'idle');

  return (
    <div className="card">
      <div className="card-head">
        <div>
          <span className="card-title">Integration Status</span>
          <div style={{ fontSize: 11.5, color: 'var(--t3)', marginTop: 2 }}>
            External API providers powering email, SMS, maps, and photo storage
          </div>
        </div>
        <button
          className={`btn btn-ghost btn-sm${checking ? ' disabled' : ''}`}
          onClick={checking ? undefined : checkAll}
          disabled={checking}
        >
          {checking ? 'Checking...' : 'Check Status'}
        </button>
      </div>
      <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {PROVIDERS.map(prov => {
          const status = statuses[prov.id];
          const detail = details[prov.id];
          const chipLabel = status === 'idle'
            ? 'Not checked'
            : status === 'checking'
            ? 'Checking...'
            : status === 'ok'
            ? 'Active'
            : 'Not configured';

          return (
            <div
              key={prov.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
                padding: '8px 0',
                borderBottom: '1px solid var(--b1)',
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--t1)' }}>
                  {prov.label}
                </div>
                {anyChecked && detail && (
                  <div style={{
                    fontSize: 11,
                    color: status === 'error' ? 'var(--rose)' : 'var(--t3)',
                    marginTop: 2,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}>
                    {status === 'error' && detail === 'Not configured'
                      ? prov.hint
                      : detail}
                  </div>
                )}
              </div>
              <ProviderChip
                status={status === 'idle' || status === 'checking' ? 'idle' : status}
                label={chipLabel}
              />
            </div>
          );
        })}
        {!anyChecked && (
          <div style={{ fontSize: 12, color: 'var(--t4)', textAlign: 'center', padding: '4px 0' }}>
            Click "Check Status" to verify each provider is configured.
          </div>
        )}
      </div>
    </div>
  );
}

function PushCard() {
  const { pushStatus, enablePush, disablePush, pingPush } = useApp();
  const supported = pushSupported();
  const granted = pushStatus === 'granted';
  return (
    <div className="card">
      <div className="card-head">
        <span className="card-title">Push Notifications</span>
        {granted && (
          <button className="btn btn-ghost btn-sm" onClick={() => pingPush({ title: 'PainterOps', body: 'Test push notification ✓', tag: 'test' })}>
            Send test
          </button>
        )}
      </div>
      <div className="card-body" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
        <div>
          <div style={{ fontSize: 13, color: 'var(--t1)', fontWeight: 600 }}>
            {!supported && 'Not supported in this browser'}
            {supported && pushStatus === 'default' && 'Notifications off'}
            {supported && pushStatus === 'granted' && '✓ Push notifications enabled'}
            {supported && pushStatus === 'denied' && 'Permission blocked'}
          </div>
          <div style={{ fontSize: 11, color: 'var(--t3)', marginTop: 4 }}>
            {supported && pushStatus === 'denied'
              ? 'Re-enable in your browser settings, then refresh.'
              : 'Get alerts for messages, arrivals, and long stops — even when PainterOps isn’t open.'}
          </div>
        </div>
        {supported && pushStatus !== 'denied' && (
          granted
            ? <button className="btn btn-ghost btn-sm" onClick={disablePush}>Disable</button>
            : <button className="btn btn-primary btn-sm" onClick={enablePush}>Enable</button>
        )}
      </div>
    </div>
  );
}

// ─── Company Profile Card ─────────────────────────────────────
const SOCIAL_LINK_KEYS = [
  { key: 'facebook',        label: 'Facebook',         placeholder: 'https://facebook.com/yourpage' },
  { key: 'instagram',       label: 'Instagram',        placeholder: 'https://instagram.com/yourhandle' },
  { key: 'google_business', label: 'Google Business',  placeholder: 'https://maps.app.goo.gl/...' },
  { key: 'yelp',            label: 'Yelp',             placeholder: 'https://yelp.com/biz/yourbiz' },
  { key: 'tiktok',          label: 'TikTok',           placeholder: 'https://tiktok.com/@yourhandle' },
  { key: 'website',         label: 'Website',          placeholder: 'https://yourcompany.com' },
];

function CompanyProfileCard() {
  const { companyProfile, userRole, updateCompanyProfile, cloudMode } = useApp();
  const [form, setForm] = useState({
    name: '', addr: '', phone: '', email: '', logo_url: '',
    social_links: {},
  });
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  // Sync form from loaded profile when it first arrives (or when company switches).
  // Don't clobber user edits — gated on !dirty.
  useEffect(() => {
    if (!companyProfile || dirty) return;
    setForm({
      name:         companyProfile.name || '',
      addr:         companyProfile.addr || '',
      phone:        companyProfile.phone || '',
      email:        companyProfile.email || '',
      logo_url:     companyProfile.logo_url || '',
      social_links: companyProfile.social_links || {},
    });
  }, [companyProfile, dirty]);

  if (!cloudMode) {
    return (
      <div className="card">
        <div className="card-head"><span className="card-title">🏢 Company Profile</span></div>
        <div className="card-body" style={{ fontSize: 12.5, color: 'var(--t3)' }}>
          Sign in to the cloud to set up your company profile. This information appears on invoices, customer notifications, and the customer portal.
        </div>
      </div>
    );
  }

  const canEdit = userRole === 'owner' || userRole === 'admin';

  const setField = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
    setDirty(true);
  };
  const setSocial = (key, value) => {
    setForm(prev => ({ ...prev, social_links: { ...prev.social_links, [key]: value } }));
    setDirty(true);
  };

  const handleSave = async () => {
    if (!form.name?.trim()) return;
    setSaving(true);
    try {
      const cleanSocial = {};
      Object.entries(form.social_links || {}).forEach(([k, v]) => {
        const val = (v || '').trim();
        if (val) cleanSocial[k] = val;
      });
      await updateCompanyProfile({
        name:         form.name.trim(),
        addr:         form.addr?.trim() || null,
        phone:        form.phone?.trim() || null,
        email:        form.email?.trim() || null,
        logo_url:     form.logo_url?.trim() || null,
        social_links: cleanSocial,
      });
      setDirty(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card">
      <div className="card-head">
        <div>
          <span className="card-title">🏢 Company Profile</span>
          <div style={{ fontSize: 11.5, color: 'var(--t3)', marginTop: 2 }}>
            Used on invoices, customer notifications, and the customer portal.
          </div>
        </div>
        {dirty && (
          <button
            className="btn btn-primary btn-sm"
            disabled={saving || !canEdit || !form.name.trim()}
            onClick={handleSave}
          >
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        )}
      </div>
      <div className="card-body" style={{ display: 'grid', gap: 12 }}>
        {!canEdit && (
          <div style={{ fontSize: 11.5, color: 'var(--amber)', padding: '6px 10px', background: 'var(--amber-t)', borderRadius: 6 }}>
            Only owners and admins can edit the company profile.
          </div>
        )}
        <div className="field">
          <label>Company name *</label>
          <input
            value={form.name}
            onChange={e => setField('name', e.target.value)}
            placeholder="e.g. Sunrise Pool Service"
            disabled={!canEdit}
          />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div className="field">
            <label>Phone</label>
            <input value={form.phone} onChange={e => setField('phone', e.target.value)} placeholder="(555) 123-4567" disabled={!canEdit} />
          </div>
          <div className="field">
            <label>Email</label>
            <input value={form.email} onChange={e => setField('email', e.target.value)} placeholder="hello@yourcompany.com" disabled={!canEdit} />
          </div>
        </div>
        <div className="field">
          <label>Address</label>
          <input value={form.addr} onChange={e => setField('addr', e.target.value)} placeholder="1234 Main St, City, ST 12345" disabled={!canEdit} />
        </div>
        <div className="field">
          <label>Logo URL</label>
          <input value={form.logo_url} onChange={e => setField('logo_url', e.target.value)} placeholder="https://… (image URL)" disabled={!canEdit} />
        </div>

        <div style={{ marginTop: 4, paddingTop: 12, borderTop: '1px solid var(--b1)' }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--t2)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Social links
          </div>
          <div style={{ display: 'grid', gap: 8 }}>
            {SOCIAL_LINK_KEYS.map(({ key, label, placeholder }) => (
              <div key={key} className="field" style={{ marginBottom: 0 }}>
                <label style={{ fontSize: 11.5 }}>{label}</label>
                <input
                  value={form.social_links?.[key] || ''}
                  onChange={e => setSocial(key, e.target.value)}
                  placeholder={placeholder}
                  disabled={!canEdit}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Settings() {
  const { plugins, togglePlugin, companyProfile } = useApp();

  return (
    <div className="page fade-up">
      <div className="ph">
        <div>
          <div className="ptitle">Settings &amp; Plugins</div>
          <div className="psub">Toggle features on or off for your account</div>
        </div>
      </div>

      {/* Plugin categories */}
      {Object.entries(PLUGIN_CATEGORIES).map(([cat, { label, description }]) => {
        const catPlugins = PLUGINS.filter(p => p.category === cat);
        const activeCount = catPlugins.filter(p => plugins[p.id]).length;
        const icon = CAT_ICONS[cat] || '🔧';

        return (
          <div key={cat} className="card">
            <div className="card-head">
              <div>
                <span className="card-title">{icon} {label}</span>
                <div style={{ fontSize: 11.5, color: 'var(--t3)', marginTop: 2 }}>{description}</div>
              </div>
              <span style={{ fontSize: 11, color: 'var(--t3)' }}>
                {activeCount}/{catPlugins.length} active
              </span>
            </div>
            <div className="card-body-0">
              {catPlugins.map(p => {
                const isOn = !!plugins[p.id];
                return (
                  <div
                    key={p.id}
                    className={`plugin-card${!isOn && !p.core ? ' disabled' : ''}`}
                  >
                    <div className="plugin-icon">{p.icon}</div>
                    <div style={{ flex: 1 }}>
                      <div className="plugin-name">
                        {p.name}
                        {p.core && (
                          <Badge
                            color="var(--t3)"
                            bg="var(--s3)"
                            style={{ marginLeft: 6 }}
                          >
                            Core
                          </Badge>
                        )}
                        {p.comingSoon && (
                          <Badge
                            color="var(--amber)"
                            bg="var(--amber-t)"
                            style={{ marginLeft: 6 }}
                          >
                            Coming soon
                          </Badge>
                        )}
                      </div>
                      <div className="plugin-desc">{p.description}</div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                      {isOn
                        ? <Badge color="var(--teal)" bg="var(--teal-t)">Active</Badge>
                        : <Badge color="var(--t3)"   bg="var(--s3)">Off</Badge>
                      }
                      <Toggle
                        on={isOn}
                        disabled={p.core || p.comingSoon}
                        onChange={() => togglePlugin(p.id)}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* Company profile */}
      <CompanyProfileCard />

      {/* Provider integration status card */}
      <ProviderStatusCard />

      {/* Push notifications card */}
      <PushCard />

      {/* Account card */}
      <div className="card">
        <div className="card-head"><span className="card-title">Account</span></div>
        <div className="card-body" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <div style={{
              fontSize: 11,
              fontWeight: 700,
              color: 'var(--t3)',
              textTransform: 'uppercase',
              letterSpacing: '.06em',
              marginBottom: 5,
            }}>
              Business Name
            </div>
            <div style={{
              background: 'var(--s2)',
              border: '1px solid var(--b2)',
              borderRadius: 8,
              padding: '9px 12px',
              fontSize: 13,
            }}>
              {companyProfile?.name}
            </div>
          </div>
          <div>
            <div style={{
              fontSize: 11,
              fontWeight: 700,
              color: 'var(--t3)',
              textTransform: 'uppercase',
              letterSpacing: '.06em',
              marginBottom: 5,
            }}>
              Current Plan
            </div>
            <div style={{
              background: 'linear-gradient(135deg,rgba(0,212,255,.08),rgba(0,240,181,.05))',
              border: '1px solid rgba(0,212,255,.2)',
              borderRadius: 8,
              padding: '9px 12px',
            }}>
              <span style={{ fontSize: 16, fontWeight: 800, color: 'var(--teal)' }}>Free trial</span>
              <span style={{ fontSize: 12, color: 'var(--t3)' }}>No billing setup yet</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
